/**
 * byok-start-dialog.tsx — "Tạo nhân viên AI bằng Key của tôi" (hộp Thêm nhân viên AI, lựa chọn 2).
 *
 * Không phải bot bên ngoài: chọn nhà cung cấp đã bật "Key của tôi" ở Nguồn AI + model → đi vào ĐÚNG luồng tạo nhân viên AI của
 * eCDP (`/ai/nhan-vien/moi?provider=&model=`), chỉ khác là token model tính vào key của khách (không trừ lượt AI).
 * Chưa kết nối key → nút mở Nguồn AI ở TAB MỚI; quay lại tab này là danh sách tự tải lại.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, ExternalLink, KeyRound, Loader2, RefreshCw } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAiModelsBySource, useAiSources } from '@/hooks/use-ai-credits'
import { AI_PATHS } from '@/lib/ai-khung'
import { byokModelsFor, byokProviders, newBotPathWithModel } from './byok-start-lib'

function openSources() {
  window.open(AI_PATHS.sources, '_blank', 'noopener')
}

export function ByokStartDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation('team')
  const navigate = useNavigate()
  const sources = useAiSources()
  const models = useAiModelsBySource(open ? 'byok' : null)
  const [provider, setProvider] = useState('')
  const [model, setModel] = useState('')

  // Quay lại tab này (sau khi kết nối key ở tab Nguồn AI) → tải lại danh sách nhà cung cấp + model.
  const refetchSources = sources.refetch
  const refetchModels = models.refetch
  useEffect(() => {
    if (!open) return
    const reload = () => { if (document.visibilityState === 'visible') { void refetchSources(); void refetchModels() } }
    window.addEventListener('focus', reload)
    document.addEventListener('visibilitychange', reload)
    return () => { window.removeEventListener('focus', reload); document.removeEventListener('visibilitychange', reload) }
  }, [open, refetchSources, refetchModels])

  const providers = useMemo(() => byokProviders(sources.data), [sources.data])
  const current = providers.some((p) => p.provider === provider) ? provider : providers[0]?.provider ?? ''
  const modelOptions = useMemo(() => byokModelsFor(models.data?.models, current), [models.data, current])
  const currentModel = modelOptions.some((m) => m.value === model) ? model : modelOptions[0]?.value ?? ''
  const loading = sources.isLoading || (!!current && models.isLoading)
  const refreshing = sources.isFetching || models.isFetching

  function next() {
    if (!current || !currentModel) return
    onOpenChange(false)
    navigate(newBotPathWithModel(current, currentModel))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /> {t('byokStartDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('byokStartDialog.description')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('byokStartDialog.loadingKeys')}</div>
        ) : providers.length === 0 ? (
          <div className="space-y-3 rounded-lg border border-dashed p-4 text-sm">
            <p className="font-medium">{t('byokStartDialog.noProvider')}</p>
            <p className="text-muted-foreground">
              <Trans t={t} i18nKey="byokStartDialog.howToConnect" components={{ b: <b /> }} />
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={openSources}><ExternalLink /> {t('byokStartDialog.connectAtSources')}</Button>
              <Button variant="outline" onClick={() => { void refetchSources(); void refetchModels() }} disabled={refreshing}>
                <RefreshCw className={refreshing ? 'animate-spin' : undefined} /> {t('common:actions.reload')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t('byokStartDialog.providerLabel')}</Label>
              <Select value={current} onValueChange={(v) => { setProvider(v); setModel('') }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {providers.map((p) => <SelectItem key={p.provider} value={p.provider}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('byokStartDialog.modelLabel')}</Label>
              <Select value={currentModel} onValueChange={setModel} disabled={!modelOptions.length}>
                <SelectTrigger><SelectValue placeholder={t('byokStartDialog.noModel')} /></SelectTrigger>
                <SelectContent>
                  {modelOptions.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <button type="button" onClick={openSources} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
              {t('byokStartDialog.addOrChangeKey')} <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common:actions.cancel')}</Button>
          {providers.length > 0 && (
            <Button onClick={next} disabled={!current || !currentModel}>{t('byokStartDialog.continueCreate')} <ArrowRight /></Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
